import type { DosagePayload } from "./dosage";
import Dosage from "./dosage";
import type { MedicationPayload } from "./medication";
import Medication from "./medication";

export interface MedicationRequestPayload {
  medication: MedicationPayload;
  dosage?: DosagePayload;
  comment?: string;
}

/**
 * https://www.hl7.org/fhir/medicationrequest.html#MedicationRequest
 */
export default class MedicationRequest {
  static fromPayload(payload: MedicationRequestPayload): MedicationRequest {
    return new MedicationRequest({
      medication: new Medication(payload.medication),
      dosage: payload.dosage ? Dosage.fromPayload(payload.dosage) : undefined,
      comment: payload.comment
    });
  }

  medication: Medication;
  dosage?: Dosage;
  comment?: string;

  constructor({
    medication,
    dosage,
    comment
  }: {
    medication: Medication;
    dosage?: Dosage;
    comment?: string;
  }) {
    this.medication = medication;
    this.dosage = dosage;
    this.comment = comment;
  }

  // FIXME: this isEqual method has been written according to the current
  // implementation of the constructor which may lead a MedicationRequest to
  // not have a Medication or a Dosage, but this may not be accurate. We
  // should look further into the usage of MedicationRequest to validate the
  // implementation in the constructor.
  isEqual(other: MedicationRequest | undefined): boolean {
    if (this == other) return true;

    let result = true;

    if (this.medication) {
      result &&= this.medication.isEqual(other?.medication);
    } else {
      result &&= Boolean(other && this.medication == other.medication);
    }

    if (this.dosage) {
      result &&= this.dosage.isEqual(other?.dosage);
    } else {
      result &&= Boolean(other && this.dosage == other.dosage);
    }

    return result;
  }

  get label(): string | undefined {
    return this.medication?.description;
  }

  get dosePerTimingUnit(): number | string | undefined {
    if (this.dosage?.timing && this.dosage.doseQuantity) {
      return this.medication.dose
        ? this.dosage.doseQuantity *
            this.dosage.timing.frequency *
            +this.medication.dose
        : this.dosage.doseQuantity * this.dosage.timing.frequency || "";
    }
    return undefined;
  }

  get jsonPayload(): MedicationRequestPayload {
    const payload: MedicationRequestPayload = {
      medication: this.medication.jsonPayload
    };

    if (this.dosage) {
      payload.dosage = this.dosage.jsonPayload;
    }

    if (this.comment) {
      payload.comment = this.comment;
    }

    return payload;
  }
}
